import { BLOCK_TYPES } from './blockTypes';

const parseStyleString = (styleString) => {
  if (!styleString) return {};
  
  const styles = {};
  styleString.split(';').forEach(rule => {
    const [property, value] = rule.split(':').map(s => s.trim());
    if (property && value) {
      const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      styles[camelCaseProperty] = value;
    }
  });
  
  return styles;
};

const parsePadding = (paddingString) => {
  if (!paddingString) return { top: 0, right: 0, bottom: 0, left: 0 };
  
  const values = paddingString.split(' ').map(v => {
    const num = parseFloat(v);
    return isNaN(num) ? 0 : num;
  });
  
  if (values.length === 1) {
    return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
  } else if (values.length === 2) {
    return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
  } else if (values.length === 3) {
    return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
  } else if (values.length === 4) {
    return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
  }
  
  return { top: 0, right: 0, bottom: 0, left: 0 };
};

const extractCommonProperties = (styles) => {
  const properties = {};
  
  if (styles.backgroundColor && styles.backgroundColor !== 'transparent') {
    properties.backgroundColor = styles.backgroundColor;
  }
  
  if (styles.color) {
    properties.color = styles.color;
  }
  
  if (styles.fontSize) {
    properties.fontSize = parseFloat(styles.fontSize);
  }
  
  if (styles.fontFamily) {
    properties.fontFamily = styles.fontFamily;
  }
  
  if (styles.textAlign) {
    properties.textAlign = styles.textAlign;
  }
  
  if (styles.fontWeight) {
    properties.fontWeight = styles.fontWeight;
  }
  
  if (styles.padding) {
    properties.padding = parsePadding(styles.padding);
  }
  
  if (styles.margin) {
    properties.margin = parsePadding(styles.margin);
  }
  
  if (styles.borderRadius) {
    properties.borderRadius = parseFloat(styles.borderRadius);
  }
  
  if (styles.lineHeight) {
    properties.lineHeight = parseFloat(styles.lineHeight);
  }
  
  return properties;
};

const generateBlockId = () => {
  return `block_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const parseHeaderBlock = (element) => {
  const headerElement = element.querySelector('h1, h2, h3, h4, h5, h6');
  if (!headerElement) return null;
  
  const styles = parseStyleString(headerElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const commonProps = extractCommonProperties({ ...tdStyles, ...styles });
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.HEADER,
    properties: {
      ...commonProps,
      level: headerElement.tagName.toLowerCase(),
      title: headerElement.textContent.trim(),
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || parsePadding(styles.margin || '0'),
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
    }
  };
};

const parseTextBlock = (element) => {
  // Try to find text element with any of the supported tags
  let textElement = element.querySelector('p');
  let htmlTag = 'p';
  
  if (!textElement) {
    textElement = element.querySelector('div');
    htmlTag = 'div';
  }
  if (!textElement) {
    textElement = element.querySelector('span');
    htmlTag = 'span';
  }
  if (!textElement) {
    textElement = element.querySelector('pre');
    htmlTag = 'pre';
  }
  
  if (!textElement) return null;
  
  // Check if content is wrapped in strong tag
  const strongElement = textElement.querySelector('strong');
  const useStrong = !!strongElement;
  const content = useStrong ? strongElement.textContent.trim() : textElement.textContent.trim();
  
  const styles = parseStyleString(textElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const commonProps = extractCommonProperties({ ...tdStyles, ...styles });
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.TEXT,
    properties: {
      ...commonProps,
      content: content,
      htmlTag: htmlTag,
      useStrong: useStrong,
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || parsePadding(styles.margin || '0'),
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
      textDecoration: 'none',
    }
  };
};

const parseListItemsBlock = (element) => {
  // Try to find list element (ul or ol)
  let listElement = element.querySelector('ul');
  let listType = 'ul';
  
  if (!listElement) {
    listElement = element.querySelector('ol');
    listType = 'ol';
  }
  
  if (!listElement) return null;
  
  const styles = parseStyleString(listElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  // Extract list items
  const listItems = listElement.querySelectorAll('li');
  const items = Array.from(listItems).map(li => li.textContent.trim());
  
  const commonProps = extractCommonProperties({ ...tdStyles, ...styles });
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.LISTITEMS,
    properties: {
      ...commonProps,
      listType: listType,
      items: items,
      listStyleType: styles.listStyleType || (listType === 'ul' ? 'disc' : 'decimal'),
      itemSpacing: 8,
      markerColor: styles.color || '#000000',
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || parsePadding(styles.margin || '0'),
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
    }
  };
};

const parseButtonBlock = (element) => {
  const linkElement = element.querySelector('a');
  if (!linkElement) return null;
  
  const styles = parseStyleString(linkElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const commonProps = extractCommonProperties(tdStyles);
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.BUTTON,
    properties: {
      ...commonProps,
      text: linkElement.textContent.trim(),
      link: linkElement.getAttribute('href') || '#',
      linkTarget: linkElement.getAttribute('target') || '_blank',
      buttonbackgroundColor: styles.backgroundColor || '#007bff',
      buttonpadding: parsePadding(styles.padding || '10px'),
      buttonmargin: parsePadding(styles.margin || '0'),
      buttonborderRadius: parseFloat(styles.borderRadius || '4'),
      fontSize: parseFloat(styles.fontSize || '16'),
      fontFamily: styles.fontFamily || 'Arial, sans-serif',
      fontWeight: styles.fontWeight || 'bold',
      color: styles.color || '#ffffff',
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || { top: 0, right: 0, bottom: 0, left: 0 },
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
      display: 'inline-block',
      width: 'auto',
    }
  };
};

const parseImageBlock = (element) => {
  const imgElement = element.querySelector('img');
  if (!imgElement) return null;
  
  const styles = parseStyleString(imgElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const commonProps = extractCommonProperties(tdStyles);
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.IMAGE,
    properties: {
      ...commonProps,
      src: imgElement.getAttribute('src') || '',
      alt: imgElement.getAttribute('alt') || '',
      width: styles.width || '100%',
      height: styles.height || 'auto',
      objectFit: 'cover',
      link: '',
      linkTarget: '_blank',
      imgborderRadius: parseFloat(styles.borderRadius || '0'),
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || { top: 0, right: 0, bottom: 0, left: 0 },
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
    }
  };
};

const parseDividerBlock = (element) => {
  const hrElement = element.querySelector('hr');
  if (!hrElement) return null;
  
  const styles = parseStyleString(hrElement.getAttribute('style'));
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const borderTop = styles.borderTop || '';
  const parts = borderTop.split(' ');
  const height = parseFloat(parts[0]) || 1;
  const color = parts[2] || '#cccccc';
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.DIVIDER,
    properties: {
      backgroundColor: 'transparent',
      height: height,
      dividerbackgroundColor: color,
      width: '100%',
      style: 'solid',
      padding: parsePadding(tdStyles.padding || '10px 0'),
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: 0,
    }
  };
};

const parseSpacerBlock = (element) => {
  const styles = parseStyleString(element.getAttribute('style'));
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.SPACER,
    properties: {
      height: parseFloat(styles.height || '20'),
      width: '100%',
      backgroundColor: 'transparent',
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
    }
  };
};

const parseHTMLContentBlock = (element) => {
  const tdStyles = parseStyleString(element.getAttribute('style'));
  
  const commonProps = extractCommonProperties(tdStyles);
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.HTMLCONTENT,
    properties: {
      ...commonProps,
      htmlContent: element.innerHTML.trim(),
      sanitize: true,
      allowedTags: ['p', 'div', 'span', 'br', 'strong', 'em', 'u', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      allowedAttributes: ['href', 'src', 'alt', 'title', 'style', 'class'],
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || { top: 0, right: 0, bottom: 0, left: 0 },
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
    }
  };
};

const parseContainerBlock = (element) => {
  const tdStyles = parseStyleString(element.getAttribute('style'));
  const nestedTable = element.querySelector(':scope > table');
  
  if (!nestedTable) return null;
  
  const children = [];
  
  // Check if it has column children (tr with multiple td elements)
  const firstRow = nestedTable.querySelector(':scope > tr, :scope > tbody > tr');
  const hasColumns = firstRow && firstRow.querySelectorAll(':scope > td').length > 1;
  
  if (hasColumns) {
    // Parse columns
    const columnCells = firstRow.querySelectorAll(':scope > td');
    columnCells.forEach(columnCell => {
      const columnBlock = parseColumnBlock(columnCell);
      if (columnBlock) {
        children.push(columnBlock);
      }
    });
  } else {
    // Parse regular nested blocks
    const rows = nestedTable.querySelectorAll(':scope > tr, :scope > tbody > tr');
    rows.forEach(row => {
      const block = parseTableRow(row);
      if (block) {
        children.push(block);
      }
    });
  }
  
  const commonProps = extractCommonProperties(tdStyles);
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.CONTAINER,
    properties: {
      backgroundColor: commonProps.backgroundColor || 'transparent',
      width: tdStyles.width || '100%',
      minHeight: parseFloat(tdStyles.minHeight || '100'),
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || parsePadding(tdStyles.margin || '0'),
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
      direction: 'column',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      gap: 10,
      maxWidth: '100%',
    },
    children
  };
};

const parseColumnBlock = (element) => {
  const tdStyles = parseStyleString(element.getAttribute('style'));
  const nestedTable = element.querySelector(':scope > table');
  
  const children = [];
  
  if (nestedTable) {
    const rows = nestedTable.querySelectorAll(':scope > tr, :scope > tbody > tr');
    rows.forEach(row => {
      const block = parseTableRow(row);
      if (block) {
        children.push(block);
      }
    });
  }
  
  const commonProps = extractCommonProperties(tdStyles);
  
  return {
    id: generateBlockId(),
    type: BLOCK_TYPES.COLUMN,
    properties: {
      backgroundColor: commonProps.backgroundColor || 'transparent',
      width: tdStyles.width || '100%',
      minHeight: parseFloat(tdStyles.minHeight || '50'),
      padding: commonProps.padding || parsePadding(tdStyles.padding || '10px'),
      margin: commonProps.margin || parsePadding(tdStyles.margin || '0'),
      border: { width: 0, style: 'solid', color: '#000000' },
      borderRadius: commonProps.borderRadius || 0,
      direction: 'column',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      gap: 10,
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '0%',
    },
    children
  };
};

const detectBlockType = (trElement) => {
  const tdElement = trElement.querySelector('td');
  if (!tdElement) return null;
  
  // Check for container (has nested table with rows or columns)
  const nestedTable = tdElement.querySelector(':scope > table');
  if (nestedTable) {
    const nestedRows = nestedTable.querySelectorAll(':scope > tr, :scope > tbody > tr');
    if (nestedRows.length > 0) {
      // Check if it's a multi-column container
      const firstRow = nestedRows[0];
      const tdCount = firstRow.querySelectorAll(':scope > td').length;
      if (tdCount > 1 || nestedRows.length > 1 || tdElement.querySelector('table table')) {
        return 'container';
      }
    }
  }
  
  if (tdElement.querySelector('h1, h2, h3, h4, h5, h6')) {
    return 'header';
  }
  
  if (tdElement.querySelector('hr')) {
    return 'divider';
  }
  
  const styles = parseStyleString(tdElement.getAttribute('style'));
  if (styles.height && tdElement.textContent.trim() === '\u00A0') {
    return 'spacer';
  }
  
  if (tdElement.querySelector('a[style*="background-color"]')) {
    return 'button';
  }
  
  if (tdElement.querySelector('img')) {
    return 'image';
  }
  
  if (tdElement.querySelector('ul, ol')) {
    return 'listitems';
  }
  
  if (tdElement.querySelector('p, div, span, pre')) {
    // Check if it's a text block (not a container or other complex structure)
    const textElement = tdElement.querySelector('p, div, span, pre');
    if (textElement && !textElement.querySelector('table')) {
      return 'text';
    }
  }
  
  return 'htmlcontent';
};

const parseTableRow = (trElement) => {
  const tdElement = trElement.querySelector('td');
  if (!tdElement) return null;
  
  const blockType = detectBlockType(trElement);
  
  switch (blockType) {
    case 'container':
      return parseContainerBlock(tdElement);
    case 'header':
      return parseHeaderBlock(tdElement);
    case 'text':
      return parseTextBlock(tdElement);
    case 'listitems':
      return parseListItemsBlock(tdElement);
    case 'button':
      return parseButtonBlock(tdElement);
    case 'image':
      return parseImageBlock(tdElement);
    case 'divider':
      return parseDividerBlock(tdElement);
    case 'spacer':
      return parseSpacerBlock(tdElement);
    case 'htmlcontent':
      return parseHTMLContentBlock(tdElement);
    default:
      return null;
  }
};

export const importHTMLToBlocks = (htmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    
    const emailContainer = doc.querySelector('.email-container');
    if (!emailContainer) {
      throw new Error('Invalid email template: email-container not found');
    }
    
    const blocks = [];
    const rows = emailContainer.querySelectorAll(':scope > tr, :scope > tbody > tr');
    
    rows.forEach(row => {
      const block = parseTableRow(row);
      if (block) {
        blocks.push(block);
      }
    });
    
    const bodyElement = doc.querySelector('body');
    const bodyStyles = bodyElement ? parseStyleString(bodyElement.getAttribute('style')) : {};
    const containerStyles = parseStyleString(emailContainer.getAttribute('style'));
    
    const globalProperties = {
      backgroundColor: containerStyles.backgroundColor || '#ffffff',
      emailWidth: parseInt(emailContainer.getAttribute('width')) || 600,
      emailBackgroundColor: bodyStyles.backgroundColor || '#e0e0e0',
      emailBorderRadius: parseFloat(containerStyles.borderRadius || '0'),
      fontFamily: bodyStyles.fontFamily || 'Arial, sans-serif',
      padding: parsePadding(containerStyles.padding || '0'),
      margin: parsePadding(containerStyles.margin || '10px auto'),
    };
    
    const preheaderElement = doc.querySelector('table[width="100%"] > tbody > tr:first-child p');
    const footerElement = doc.querySelector('table[width="100%"] > tbody > tr:last-child p');
    
    if (preheaderElement) {
      globalProperties.preheaderText = preheaderElement.textContent.trim();
    }
    
    if (footerElement) {
      globalProperties.footerText = footerElement.textContent.trim();
    }
    
    return {
      blocks,
      globalProperties,
      success: true,
      message: `Successfully imported ${blocks.length} blocks`
    };
  } catch (error) {
    return {
      blocks: [],
      globalProperties: {},
      success: false,
      message: `Failed to import HTML: ${error.message}`
    };
  }
};

export const validateHTMLTemplate = (htmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    
    const errors = [];
    const warnings = [];
    
    if (!doc.querySelector('.email-container')) {
      errors.push('Missing email-container class in the HTML template');
    }
    
    if (!doc.querySelector('body')) {
      errors.push('Invalid HTML structure: missing body tag');
    }
    
    const emailContainer = doc.querySelector('.email-container');
    if (emailContainer) {
      const rows = emailContainer.querySelectorAll(':scope > tr, :scope > tbody > tr');
      if (rows.length === 0) {
        warnings.push('No content blocks found in the email template');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Invalid HTML: ${error.message}`],
      warnings: []
    };
  }
};
